/**
 * 規則ベースの事件番号抽出器（決定論的・AI 不要）。
 *
 * 日本の弁護士が実際に貼り付ける表記の大半は、これだけで 100% 判定できる。
 * AI は「同年」「並列省略」など、規則で取り切れなかった場合のフォールバックとしてのみ使う。
 */

import {
  BARE_SYMBOLS,
  CONNECTORS,
  ERA_ALIASES,
  MIN_WESTERN_YEAR,
  SCOPE_BREAK_CHARS,
  SYMBOL_CHAR_CLASS,
  TRAILING_UNIT_BLOCKLIST,
} from './constants';
import { westernToEra } from './normalize';
import { KANJI_NUMBER_CHARS, parseJapaneseNumber } from './numerals';
import { sanitizePreservingLength } from './sanitize';
import type { EraName, ExtractedCaseNumber, UnresolvedCandidate } from './types';

const K = KANJI_NUMBER_CHARS.replace(/[\]\\^-]/g, (m) => `\\${m}`);

const ERA_TOKEN = '(?:明治|大正|昭和|平成|令和|明|大|昭|平|令|[MTSHRmtshr])';
const YEAR_NUM = `(?:[0-9]{1,4}|元|[${K}]{1,6})`;
const SERIAL_NUM = `(?:[0-9]{1,7}|[${K}]{1,9})`;
const SYM_IN = `[${SYMBOL_CHAR_CLASS}]{1,4}`;

const BARE_ALT = `(?:${[...BARE_SYMBOLS].sort((a, b) => b.length - a.length).join('|')})`;

const YEAR_PART =
  `(?:(?<dou>同年)` +
  `|(?<era>${ERA_TOKEN})\\s*(?<y1>${YEAR_NUM})\\s*年?` +
  `|(?<y2>[0-9]{4})\\s*年?` +
  `|(?<y3>${YEAR_NUM})\\s*年)`;

const SYM_PART = `(?:\\(\\s*(?<sb>${SYM_IN})\\s*\\)|(?<sn>${BARE_ALT}))`;

/** Tier A: 年 + 事件符号 + 通し番号（完全形） */
const TIER_A = `${YEAR_PART}\\s*${SYM_PART}\\s*(?:第\\s*)?(?<aserial>${SERIAL_NUM})\\s*号?`;

/** Tier B: 事件符号（括弧付き）+ 通し番号。年は文脈から引き継ぐ必要がある */
const TIER_B =
  `\\(\\s*(?<bsb>${SYM_IN})\\s*\\)\\s*` +
  `(?:第\\s*(?<bs1>${SERIAL_NUM})\\s*号?|(?<bs2>${SERIAL_NUM})\\s*号)`;

/** Tier C: 通し番号のみ。直前の接続表現と文脈が揃っている場合だけ採用する */
const TIER_C = `第\\s*(?<cs>${SERIAL_NUM})\\s*号`;

const MASTER_RE = new RegExp(`${TIER_A}|${TIER_B}|${TIER_C}`, 'g');

/** 「〜令」で終わる語の直後を元号「令」と誤認しないためのブロックリスト。 */
const BAD_ERA_PREFIX = new Set(['命', '政', '省', '法', '勅', '訓', '指', '発', '条', '号', '布']);

interface ScopeContext {
  era: EraName;
  year: number;
  symbol: string;
  index: number;
}

function isSpace(ch: string): boolean {
  return ch === ' ' || ch === '\t' || ch === '　';
}

function scopeStartIndex(text: string, pos: number): number {
  for (let i = pos - 1; i >= 0; i -= 1) {
    if (SCOPE_BREAK_CHARS.has(text[i])) return i + 1;
  }
  return 0;
}

function precededByConnector(text: string, pos: number): boolean {
  let i = pos - 1;
  while (i >= 0 && isSpace(text[i])) i -= 1;
  if (i < 0) return false;
  const tail = text.slice(Math.max(0, i - 5), i + 1);
  return CONNECTORS.some((c) => tail.endsWith(c));
}

function trailingCharBlocked(text: string, matched: string, end: number): boolean {
  const endsWithGo = matched.trimEnd().endsWith('号');
  if (endsWithGo) return false;
  let i = end;
  while (i < text.length && isSpace(text[i])) i += 1;
  if (i >= text.length) return false;
  return TRAILING_UNIT_BLOCKLIST.has(text[i]);
}

/** 直前が英数字（`Room5` のような誤検出）や「〜令」でないことを確認する。 */
function eraPrefixOk(text: string, eraToken: string, pos: number): boolean {
  if (pos === 0) return true;
  const prev = text[pos - 1];
  if (/^[A-Za-z]$/.test(eraToken)) {
    return !/[A-Za-z0-9]/.test(prev);
  }
  if (eraToken.length === 1) {
    return !BAD_ERA_PREFIX.has(prev);
  }
  return true;
}

function distinctYearContexts(contexts: ScopeContext[]): { era: EraName; year: number }[] {
  const seen = new Map<string, { era: EraName; year: number }>();
  for (const c of contexts) {
    seen.set(`${c.era}-${c.year}`, { era: c.era, year: c.year });
  }
  return [...seen.values()];
}

export interface RuleExtractionResult {
  candidates: ExtractedCaseNumber[];
  unresolved: UnresolvedCandidate[];
}

export function extractWithRules(originalText: string): RuleExtractionResult {
  const text = sanitizePreservingLength(originalText);
  const candidates: ExtractedCaseNumber[] = [];
  const unresolved: UnresolvedCandidate[] = [];
  const contexts: ScopeContext[] = [];
  /**
   * 年が書かれているのに元号を確定できなかった箇所の位置。
   * 「令和5年（ワ）第1号、2019（受）2207、（受）第99号」のように、間に
   * 確定できない年が挟まっている場合、省略表記がどの年を引き継ぐかは判断できない。
   */
  const ambiguousYearMarks: number[] = [];

  MASTER_RE.lastIndex = 0;
  let m: RegExpExecArray | null;

  while ((m = MASTER_RE.exec(text)) !== null) {
    if (m[0].length === 0) {
      MASTER_RE.lastIndex += 1;
      continue;
    }
    const g = m.groups ?? {};
    const start = m.index;
    const end = start + m[0].length;
    const fragment = originalText.slice(start, end).trim();
    const scope = scopeStartIndex(text, start);
    const inScope = contexts.filter((c) => c.index >= scope && c.index < start);
    const blockedByAmbiguousYear = ambiguousYearMarks.some((i) => i >= scope && i < start);

    /** この一致を採用しない場合、内側に別の一致が隠れている可能性があるため 1 文字進めて再走査する。 */
    const reject = (): void => {
      MASTER_RE.lastIndex = start + 1;
    };

    if (trailingCharBlocked(text, m[0], end)) {
      reject();
      continue;
    }

    // ---------- Tier C: 「第1235号」 ----------
    if (g.cs !== undefined) {
      if (!precededByConnector(text, start)) {
        // 法令番号・文書管理番号などのノイズは黙って捨てる
        reject();
        continue;
      }
      if (inScope.length === 0) {
        reject();
        continue;
      }
      const years = distinctYearContexts(inScope);
      const nearest = inScope[inScope.length - 1];
      if (years.length !== 1 || blockedByAmbiguousYear) {
        unresolved.push({
          original_fragment: fragment,
          reason_code: 'ambiguous_context',
          reason: '直前に複数の年が記載されているため、どの年を引き継ぐか判定できません。',
        });
        continue;
      }
      pushCandidate(candidates, {
        original_fragment: fragment,
        calendar_type: 'era',
        era_raw: years[0].era,
        year_raw: String(years[0].year),
        symbol_raw: nearest.symbol,
        serial_raw: g.cs,
        context_inferred: true,
        source_index: start,
      });
      recordContext(contexts, years[0].era, years[0].year, nearest.symbol, start);
      continue;
    }

    // ---------- Tier B: 「（ネ）第567号」 ----------
    if (g.bsb !== undefined) {
      const serial = g.bs1 ?? g.bs2 ?? '';
      const symbol = g.bsb;
      if (inScope.length === 0) {
        unresolved.push({
          original_fragment: fragment,
          reason_code: 'missing_year',
          reason: '年の記載がなく、前後の文からも年を特定できません。',
        });
        continue;
      }
      if (!precededByConnector(text, start)) {
        unresolved.push({
          original_fragment: fragment,
          reason_code: 'ambiguous_context',
          reason: '前の事件番号から年を引き継ぐ表記かどうかを、文脈から確定できません。',
        });
        continue;
      }
      const years = distinctYearContexts(inScope);
      if (years.length !== 1 || blockedByAmbiguousYear) {
        unresolved.push({
          original_fragment: fragment,
          reason_code: 'ambiguous_context',
          reason: '直前に複数の年が記載されているため、どの年を引き継ぐか判定できません。',
        });
        continue;
      }
      pushCandidate(candidates, {
        original_fragment: fragment,
        calendar_type: 'era',
        era_raw: years[0].era,
        year_raw: String(years[0].year),
        symbol_raw: symbol,
        serial_raw: serial,
        context_inferred: true,
        source_index: start,
      });
      recordContext(contexts, years[0].era, years[0].year, symbol, start);
      continue;
    }

    // ---------- Tier A ----------
    const symbol = g.sb ?? g.sn ?? '';
    const serial = g.aserial ?? '';
    if (symbol.length === 0 || serial.length === 0) {
      reject();
      continue;
    }

    // 「同年」
    if (g.dou !== undefined) {
      const years = distinctYearContexts(inScope);
      if (years.length === 0) {
        unresolved.push({
          original_fragment: fragment,
          reason_code: 'missing_year',
          reason: '「同年」が指す年を、同一文・同一段落内から特定できません。',
        });
        continue;
      }
      if (years.length > 1 || blockedByAmbiguousYear) {
        unresolved.push({
          original_fragment: fragment,
          reason_code: 'ambiguous_context',
          reason: '直前に複数の年が記載されているため、「同年」がどの年を指すか判定できません。',
        });
        continue;
      }
      pushCandidate(candidates, {
        original_fragment: fragment,
        calendar_type: 'era',
        era_raw: years[0].era,
        year_raw: String(years[0].year),
        symbol_raw: symbol,
        serial_raw: serial,
        context_inferred: true,
        source_index: start,
      });
      recordContext(contexts, years[0].era, years[0].year, symbol, start);
      continue;
    }

    const eraToken = g.era;
    if (eraToken !== undefined && !eraPrefixOk(text, eraToken, start)) {
      reject();
      continue;
    }

    const yearRaw = g.y1 ?? g.y2 ?? g.y3 ?? '';
    if (yearRaw.length === 0) {
      reject();
      continue;
    }

    const calendarType: 'era' | 'western' =
      eraToken !== undefined ? 'era' : g.y2 !== undefined ? 'western' : 'era';

    const candidate: ExtractedCaseNumber = {
      original_fragment: fragment,
      calendar_type: calendarType,
      era_raw: eraToken ?? null,
      year_raw: yearRaw,
      symbol_raw: symbol,
      serial_raw: serial,
      context_inferred: false,
      source_index: start,
    };
    pushCandidate(candidates, candidate);

    // 文脈引き継ぎ用にこの事件番号の年・符号を覚える
    const resolvedEra = resolveEraForContext(eraToken, yearRaw);
    if (resolvedEra) {
      recordContext(contexts, resolvedEra.era, resolvedEra.year, symbol, start);
    } else {
      // 年は書かれているが元号を確定できなかった（境界の西暦など）
      ambiguousYearMarks.push(start);
    }
  }

  return { candidates, unresolved };
}

function pushCandidate(list: ExtractedCaseNumber[], c: ExtractedCaseNumber): void {
  list.push(c);
}

function recordContext(
  contexts: ScopeContext[],
  era: EraName,
  year: number,
  symbol: string,
  index: number,
): void {
  contexts.push({ era, year, symbol, index });
}

function resolveEraForContext(
  eraToken: string | undefined,
  yearRaw: string,
): { era: EraName; year: number } | null {
  const yearNum = yearRaw === '元' ? 1 : parseJapaneseNumber(yearRaw);
  if (yearNum === null || yearNum <= 0) return null;
  if (eraToken) {
    const era = ERA_ALIASES[eraToken];
    if (!era) return null;
    return { era, year: yearNum };
  }
  if (yearNum >= MIN_WESTERN_YEAR) {
    const converted = westernToEra(yearNum);
    if (converted.ok) return { era: converted.era, year: converted.eraYear };
  }
  return null;
}
